// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.UsersRoleSetup.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import { observable, action, computed } from 'mobx';
import tracker from 'share/tracking/Izenda.Tracker';
import uiState from 'logic/Izenda.UiState';

import { DEFAULT_VALUE } from 'logic/data/Izenda.UserAccountConstants';
import { concatArray } from 'share/utils/Izenda.LazyLoadingUtils';
import PagingModel from 'share/components/controls/DataGrid/helpers/Izenda.PagingModel';
import { escapeRegExp, isNil, filter, flow, get, some, map, head, forEach } from 'lodash/fp';

/**
 * User inside Role setup
 */
@tracker
class User {
  id: string;
  userName: string;
  emailAddress: string;
  firstName: string;
  lastName: string;
  fullName: string;
  state: number;

  /**
   * Indicates whether the user is assigned to the role
   */
  @observable assigned: boolean;

  constructor({ id, userName, emailAddress, firstName, lastName, fullName, state }, assigned: boolean) {
    this.id = id;
    this.userName = userName;
    this.emailAddress = emailAddress;
    this.firstName = firstName;
    this.lastName = lastName;
    this.fullName = fullName;
    this.state = state;
    this.assigned = assigned;
  }

  /**
   * Toggle the assigment status of user to the role
   */
  @action
  toggleAssign() {
    this.assigned = !this.assigned;
    this.markDirty();
  }
}

/* UsersRoleSetup model */
@tracker
export default class UsersRoleSetup {
  /**
   * The available users. Normally it's all users of a tenant
   */
  @observable usersList: Array<User>;

  /**
   * The ID of assigned users
   */
  @observable selectedUserIds: Array<string>;

  /**
   * The filtered user name
   */
  @observable searchedUserName: string;

  /**
   * Indicates the lazy loading has reached the last page
   */
  @observable isLastPage: boolean;

  /**
   * The pagination info using by lazy loading
   */
  @observable pageInfo: PagingModel;

  /**
   * A filtered user list
   */
  @computed
  get filterUser() {
    const filterRegex = new RegExp(escapeRegExp(this.searchedUserName), 'i');
    return isNil(this.searchedUserName)
      ? this.usersList
      : filter(flow(get(['fullName']), filterRegex.test.bind(filterRegex)), this.usersList);
  }

  /**
   * Indicates the users role setup has been changed
   */
  @computed
  get hasChanged() {
    return this.isDirty || some('isDirty', this.usersList);
  }

  /**
   * Gets selected users object however it just has id property.
   * Used for posting back to API for saving assigned users of the role
   */
  get selectedUsers() {
    return map(id => {
      return { id };
    }, this.selectedUserIds);
  }

  constructor(selectedUsers: Array<any>) {
    this.reload(selectedUsers);
  }

  /**
   * Appending the user list from lazy loading
   * @param  {Object} result user lazy loading list result
   * @param  {boolean} isSearching
   */
  @action
  populateLazyUserList({ result, isLastPage, total }, isSearching) {
    this.isLastPage = isLastPage;
    this.pageInfo.totalRecords = total;

    isSearching && (this.usersList = []);

    const newUsers = map(user => {
      const assigned = this.selectedUserIds.indexOf(user.id) >= 0;
      return new User(user, assigned);
    }, result);

    const selectedTenantId = uiState.getTenantId();
    const userTenantId = flow(head, get('tenantId'))(result);

    userTenantId === selectedTenantId && concatArray(this.usersList, newUsers, 'id', !isLastPage);

    return this.usersList;
  }

  /**
   * Update searched user name
   * @param  {string} text
   */
  @action
  updateSearchedUserName(text) {
    this.searchedUserName = text;
  }

  /**
   * Toggle user assignment to the role
   * @param {User} user 
   */
  @action
  toggleAssignUser(user: User) {
    if (user.assigned) {
      this.selectedUserIds.remove(user.id);
    } else {
      this.selectedUserIds.push(user.id);
    }

    user.toggleAssign();
    this.markDirty();
  }

  @action
  cleanDirty() {
    forEach(user => {
      user.resetDirty && user.resetDirty();
    }, this.usersList);
    this.resetDirty();
  }

  /**
   * Reset the state of users role setup when changing selecting role
   * @param {Array<any>} selectedUsers 
   */
  @action
  reload(selectedUsers: Array<any>) {
    this.usersList = [];
    this.selectedUserIds = selectedUsers !== null ? map(get('id'), selectedUsers) : [];
    this.pageInfo = new PagingModel(DEFAULT_VALUE.SORT_DIRS, DEFAULT_VALUE.SEARCH_CRITERIA);
    this.isLastPage = false;
    this.searchedUserName = '';
  }
}



// WEBPACK FOOTER //
// ./ui/logic/models/Izenda.UsersRoleSetup.js